import React, { useCallback, useMemo, useState } from 'react';
import { Text } from '@goodfynd/ui';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
} from '@mui/material';

import type { InputCheckboxListProps } from '../types';

import { StyledLabel } from './styles';

export default function InputCheckboxListControl<T = string>({
  defaultValue,
  label,
  max = 0,
  name,
  options,
  setValue,
}: Omit<InputCheckboxListProps<T>, 'control'>) {
  const defaultOptions = useMemo(
    () =>
      options
        .filter((option) => Array.isArray(defaultValue) && defaultValue.includes(option.value))
        .map((option) => option.value),
    [defaultValue, options]
  );
  const [selectedOptions, setSelectedOptions] = useState<(number | string)[]>(
    () => {
      setValue?.(name, defaultOptions);
      return defaultOptions;
    }
  );
  const [maxReached, setMaxReached] = useState(
    max > 0 && defaultOptions.length === max
  );

  const handleSelect = useCallback(
    (selectedOption: InputOption) => {
      const selectedValue = selectedOption.value;
      setSelectedOptions((options) => {
        const alreadySelected = options.includes(selectedValue);
        const newOptions = alreadySelected
          ? options.filter((option) => option !== selectedValue)
          : [...options, selectedValue];

        setValue?.(name, newOptions);
        setMaxReached(max > 0 && newOptions.length === max);
        return newOptions;
      });
    },
    [max, name, setValue]
  );

  return (
    <FormControl fullWidth variant="outlined">
      {typeof label === 'string' ? (
        <FormLabel component="legend">{label}</FormLabel>
      ) : (
        label
      )}

      {options.map((option: InputOption) => {
        return (
          <FormControlLabel
            key={option.value}
            control={
              <Checkbox
                defaultChecked={defaultOptions.includes(option.value)}
                disabled={!selectedOptions.includes(option.value) && maxReached}
                onChange={() => handleSelect(option)}
              />
            }
            label={
              <StyledLabel>
                <Text as="body1">{option.label}</Text>
                <Text>{option.labelRight}</Text>
              </StyledLabel>
            }
          />
        );
      })}
    </FormControl>
  );
}
